import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { COMPANY } from '../../config/company';
import QRWidget from '../common/QRWidget';

const navLinks = [
  {
    to: '/', label: 'Home', exact: true, color: '#2563eb',
    icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
  },
  {
    to: '/catalog', label: 'Catalog', color: '#0891b2',
    icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>,
  },
  {
    to: '/about', label: 'About Us', color: '#7c3aed',
    icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.58-7 8-7s8 3 8 7" /></svg>,
  },
  {
    to: '/contact', label: 'Contact', color: '#059669',
    icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
  },
];

const FOOTER_SERVICES = COMPANY.services;

export default function PublicLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [headerSearch, setHeaderSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 12);
      setCtaVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Reset CTA dismiss on page change and scroll to top on route change
  useEffect(() => {
    setCtaDismissed(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif' }}>

      {/* ── Topbar ────────────────────────────────────────── */}
      <div className="pub-topbar" style={{ background: '#0a1628', borderBottom: '1px solid #1e3a5f' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '8px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: '0.77rem', color: '#64748b' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="12" height="12" fill="#60a5fa" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
              <span style={{ color: '#94a3b8' }}>{COMPANY.address.oneLine}</span>
            </span>
            <span style={{ color: '#1e3a5f' }}>|</span>
            <span style={{ color: '#94a3b8' }}>Mon – Sat &nbsp;9:30 AM – 6:00 PM IST</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: '0.77rem' }}>
            <a href={`mailto:${COMPANY.email}`}
              style={{ color: '#60a5fa', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
              {COMPANY.email}
            </a>
            <span style={{ color: '#1e3a5f' }}>|</span>
            <a href={COMPANY.social.whatsapp} target="_blank" rel="noreferrer"
              style={{ color: '#4ade80', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      {/* ── Sticky Header ─────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.09)' : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.25s ease',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(16px, 4vw, 32px)', height: 86, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
            <img src="/prasilabs-logo.png" alt="PRASI LABS" style={{ height: 80, width: 'auto' }} />
          </Link>

          {/* Desktop Nav */}
          <nav className="pub-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {navLinks.map(link => (
              <NavLink key={link.to} to={link.to} end={link.exact}
                style={({ isActive }) => ({
                  padding: '8px 20px', borderRadius: 10, textDecoration: 'none',
                  fontSize: '0.95rem', fontWeight: isActive ? 700 : 500,
                  color: isActive ? link.color : '#374151',
                  background: isActive ? link.color + '14' : 'transparent',
                  letterSpacing: '0.01em',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: 7,
                })}
                onMouseEnter={e => { e.currentTarget.style.color = link.color; e.currentTarget.style.background = link.color + '12'; }}
                onMouseLeave={e => {
                  // let NavLink re-apply active styles on leave — read isActive from data attr workaround via class
                  const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                  e.currentTarget.style.color = isActive ? link.color : '#374151';
                  e.currentTarget.style.background = isActive ? link.color + '14' : 'transparent';
                }}>
                <span style={{ color: link.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div className="pub-nav-desktop" style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search products..."
                value={headerSearch}
                onChange={e => setHeaderSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { navigate(`/catalog?search=${encodeURIComponent(headerSearch)}`); setHeaderSearch(''); } }}
                style={{ width: 180, padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', outline: 'none' }}
              />
              <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <Link to="/contact" className="pub-nav-desktop"
              style={{ padding: '8px 18px', border: '1.5px solid #1d4ed8', borderRadius: 8, color: '#1d4ed8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1d4ed8'; }}>
              Request Quote
            </Link>
            {user ? (
              <button onClick={() => navigate('/admin/dashboard')}
                style={{ padding: '8px 20px', borderRadius: 8, background: '#1d4ed8', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Dashboard →
              </button>
            ) : (
              <button onClick={() => navigate('/login')}
                style={{ padding: '8px 20px', borderRadius: 8, background: '#1d4ed8', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                Sign In
              </button>
            )}
            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(v => !v)} className="pub-hamburger"
              style={{ display: 'none', background: 'none', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '7px 9px', borderRadius: 8, color: '#374151' }}>
              {mobileOpen
                ? <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                : <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
              }
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', padding: '14px 24px 20px' }}>
            {navLinks.map(link => (
              <NavLink key={link.to} to={link.to} end={link.exact} onClick={() => setMobileOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', borderRadius: 10, textDecoration: 'none',
                  fontSize: '0.95rem', fontWeight: isActive ? 700 : 500,
                  color: isActive ? link.color : '#374151',
                  background: isActive ? link.color + '12' : 'transparent',
                  marginBottom: 4,
                })}>
                <span style={{ color: link.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
            <div style={{ padding: '0 14px', marginBottom: 14 }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={headerSearch}
                  onChange={e => setHeaderSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { setMobileOpen(false); navigate(`/catalog?search=${encodeURIComponent(headerSearch)}`); setHeaderSearch(''); } }}
                  style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                />
                <svg width="16" height="16" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
            </div>
            <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
              <Link to="/contact" onClick={() => setMobileOpen(false)}
                style={{ flex: 1, padding: '11px', textAlign: 'center', border: '1.5px solid #1d4ed8', borderRadius: 8, color: '#1d4ed8', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 700 }}>
                Request Quote
              </Link>
              <button onClick={() => { setMobileOpen(false); navigate(user ? '/admin/dashboard' : '/login'); }}
                style={{ flex: 1, padding: '11px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                {user ? 'Dashboard' : 'Sign In'}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ── Main Content ──────────────────────────────────── */}
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>

      {/* ── Sticky Floating CTA ───────────────────────────── */}
      {ctaVisible && !ctaDismissed && (
        <div className="pub-float-cta" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10,
          animation: 'cta-slide-in 0.35s ease',
        }}>
          {/* WhatsApp */}
          <a href={COMPANY.social.whatsapp} target="_blank" rel="noreferrer"
            title="Chat on WhatsApp"
            style={{
              width: 52, height: 52, borderRadius: '50%',
              background: '#25D366', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(37,211,102,0.5)',
              textDecoration: 'none', transition: 'transform 0.18s, box-shadow 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 6px 26px rgba(37,211,102,0.65)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.5)'; }}>
            <svg width="26" height="26" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>
          {/* Request Quote */}
          <Link to="/contact"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '13px 20px', background: '#1d4ed8', color: '#fff',
              borderRadius: 30, fontWeight: 700, fontSize: '0.85rem',
              textDecoration: 'none', boxShadow: '0 4px 20px rgba(29,78,216,0.45)',
              whiteSpace: 'nowrap', transition: 'transform 0.18s, box-shadow 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 6px 26px rgba(29,78,216,0.6)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(29,78,216,0.45)'; }}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
            Request Quote
          </Link>
          {/* Dismiss */}
          <button onClick={() => setCtaDismissed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.7rem', padding: '2px 4px', lineHeight: 1 }}>
            ✕ hide
          </button>
        </div>
      )}

      {/* ── Footer ───────────────────────────────────────── */}
      <footer style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 52%, #ffffff 100%)', color: '#0f172a', borderTop: '1px solid #3b82f6', paddingBottom: '8px', marginBottom: '0', marginTop: '12px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', padding: 'clamp(16px, 2vw, 30px) clamp(14px, 2vw, 28px) 18px' }}>

          {/* 4-column grid */}
          <div className="pub-footer-grid" style={{ gap: '18px', alignItems: 'flex-start' }}>

            {/* Brand */}
            <div>
              <img src="/prasilabs-logo.png" alt="PRASI LABS"
                style={{ height: 48, filter: 'none', opacity: 1, marginBottom: 14 }} />
              <p style={{ fontSize: '0.75rem', lineHeight: 1.4, color: '#475569', marginBottom: 12 }}>
                {COMPANY.description}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={COMPANY.social.linkedin} target="_blank" rel="noreferrer"
                  style={{ width: 36, height: 36, borderRadius: 9, background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#93c5fd', textDecoration: 'none', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#1e3a5f'}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                </a>
                <a href={COMPANY.social.whatsapp} target="_blank" rel="noreferrer"
                  style={{ width: 36, height: 36, borderRadius: 9, background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4ade80', textDecoration: 'none', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                  onMouseLeave={e => e.currentTarget.style.background = '#1e3a5f'}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                </a>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', marginBottom: 18, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Navigation</div>
              {navLinks.map(link => (
                <NavLink key={link.to} to={link.to} end={link.exact}
                  style={({ isActive }) => ({
                    display: 'block', fontSize: '0.9rem', textDecoration: 'none', marginBottom: 11, transition: 'color 0.15s',
                    color: isActive ? '#1d4ed8' : '#1f2937',
                    fontWeight: isActive ? 700 : 500,
                  })}
                  onMouseEnter={e => e.currentTarget.style.color = '#1d4ed8'}
                  onMouseLeave={e => {
                    if (!e.currentTarget.classList.contains('active')) {
                      e.currentTarget.style.color = '#1f2937';
                    }
                  }}>
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Services */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', marginBottom: 18, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Services</div>
              {FOOTER_SERVICES.map(s => (
                <div key={s} style={{ fontSize: '0.9rem', color: '#1f2937', marginBottom: 11, display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
                  {s}
                </div>
              ))}
            </div>

            {/* Contact */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', marginBottom: 18, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Get in Touch</div>
              <div style={{ fontSize: '0.9rem', lineHeight: 1.35 }}>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ color: '#1f2937', fontWeight: 600, marginBottom: 4, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</div>
                  <div style={{ color: '#1f2937', lineHeight: 1.7 }}>{COMPANY.address.line1}<br />{COMPANY.address.line2}<br />{COMPANY.address.city} – {COMPANY.address.pin}, {COMPANY.address.state}</div>
                </div>
              </div>
              {/* WhatsApp QR */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginTop: 16, borderTop: '1px solid #cbd5e1', paddingTop: 14 }}>
                <div style={{ flex: '1 1 0' }}>
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.35, marginBottom: 6 }}>
                    <a href={`mailto:${COMPANY.email}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{COMPANY.email}</a>
                  </div>
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.35, marginBottom: 6 }}>
                    <a href={`tel:${COMPANY.phone}`} style={{ color: '#1f2937', textDecoration: 'none' }}>{COMPANY.phone}</a>
                  </div>
                  <div style={{ color: '#334155', fontSize: '0.78rem' }}>{COMPANY.hours}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <QRWidget
                    value={COMPANY.social.whatsapp}
                    size={90}
                    dark="#0f172a"
                    light="#f8fafc"
                    label="WhatsApp Quick Chat"
                    style={{ gap: 8 }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: 600 }}></span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: '0.68rem', color: '#334155' }}>
              &copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
            </div>
            <div style={{ fontSize: '0.68rem', color: '#334155', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>CIN: {COMPANY.cin}</span>
              <span style={{ color: '#1e3a5f' }}>•</span>
              <span>GST: {COMPANY.gstin}</span>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 768px) {
          .pub-nav-desktop { display: none !important; }
          .pub-hamburger   { display: flex !important; }
          .pub-topbar      { display: none !important; }
        }
        @keyframes cta-slide-in {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
